import { Field, Field_Numeric_Unit } from 'hiber-grpc/src/customer_compiled/field';
import { Slot, VNode, Comment, Fragment, Text } from 'vue';
import { OrganizationPermission, SupportPermission, UserPermission } from 'hiber-grpc/src/customer_compiled/permission';
import { Health } from 'hiber-grpc/src/customer_compiled/base';
import {
  Value_Numeric_BatteryLevel_BatteryLevelUnit,
  Value_Numeric_Distance_DistanceUnit,
  Value_Numeric_Duration_DurationUnit,
  Value_Numeric_Flow_FlowUnit,
  Value_Numeric_FuelEfficiency_FuelEfficiencyUnit,
  Value_Numeric_Mass_MassUnit,
  Value_Numeric_Percentage_PercentageUnit,
  Value_Numeric_Pressure_PressureUnit,
  Value_Numeric_Rate_RateUnit,
  Value_Numeric_RotationSpeed_RotationSpeedUnit,
  Value_Numeric_Speed_SpeedUnit,
  Value_Numeric_Temperature_TemperatureUnit,
  Value_Numeric_Type,
  Value_Numeric_Voltage_VoltageUnit,
  Value_Numeric_Volume_VolumeUnit,
} from 'hiber-grpc/src/customer_compiled/value';
import enumValuesClean from '@/utils/enum';

function asArray(arg) {
  if (Array.isArray(arg)) {
    return arg;
  }

  return arg !== null ? [arg] : [];
}

export const DATE_FORMAT = 'd MMM `yy HH:mm';

export enum State {
  LOADING = 'LOADING',
  READY = 'READY',
  DEFAULT = 'DEFAULT',
}

export const ALL_SENSORS_TAB_NAME = 'all';

export const EM_DASH = '—';

export function createUint8Array(string: string): Uint8Array {
  const contentBytes: number[] = [];
  for (let i = 0; i < string.length; i += 1) {
    contentBytes.push(string.charCodeAt(i));
  }

  return new Uint8Array(contentBytes);
}

export function convertHexToAscii(value: string | undefined): string {
  if (!value) return '';
  const hex = value.replace(/\s+/g, '');
  let str = '';
  for (let n = 0; n < hex.length; n += 2) {
    str += String.fromCharCode(parseInt(hex.substr(n, 2), 16));
  }
  return str;
}

export type DateRange = {
  start: Date | string;
  end: Date | string;
};

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type WithModemInfo = {
  modemNumber?: string;
  modemName?: string;
};
export type WithProcessPointInfo = {
  processPointIdentifier?: string;
  processPointName?: string;
};
export type WithColor = {
  color: string;
};
export type EnhancedField = Field & WithColor & WithModemInfo & WithProcessPointInfo;

function isVNodeEmpty(vnode: VNode[] | undefined): boolean {
  return (
    !vnode ||
    asArray(vnode).every(
      (vnode) =>
        vnode.type === Comment ||
        (vnode.type === Text && !vnode.children.trim()) ||
        (vnode.type === Fragment && isVNodeEmpty(vnode.children)),
    )
  );
}

function isSlotEmpty(slot: Slot | undefined, props = {}) {
  return isVNodeEmpty(slot?.(props));
}

export function hasSlotContent(slot: Slot | undefined, props = {}): boolean {
  return !isSlotEmpty(slot, props);
}

export const permissionsTotalCount = Object.keys(OrganizationPermission).filter(
  (i) => i !== OrganizationPermission.UNRECOGNIZED && i !== OrganizationPermission.PERMISSION_DEFAULT,
).length;
export const supportPermissionsTotalCount = Object.keys(SupportPermission).filter(
  (i) => i !== SupportPermission.UNRECOGNIZED,
).length;
export const userPermissionsTotalCount = Object.keys(UserPermission).filter(
  (i) => i !== UserPermission.UNRECOGNIZED,
).length;

export const getHealthColor = (health: Health): string => {
  switch (health) {
    case Health.ERROR:
      return 'bg-alert-red';
    case Health.WARNING:
      return 'bg-alert-orange';
    case Health.OK:
      return 'bg-alert-green';
    default:
      return '';
  }
};

export function unitForType(numericValueType: Value_Numeric_Type, exclude: string[] = []): string[] {
  switch (numericValueType) {
    case Value_Numeric_Type.BATTERY_LEVEL:
      return enumValuesClean(Value_Numeric_BatteryLevel_BatteryLevelUnit, exclude);
    case Value_Numeric_Type.VOLUME:
      return enumValuesClean(Value_Numeric_Volume_VolumeUnit, exclude);
    case Value_Numeric_Type.FLOW:
      return enumValuesClean(Value_Numeric_Flow_FlowUnit, exclude);
    case Value_Numeric_Type.SPEED:
      return enumValuesClean(Value_Numeric_Speed_SpeedUnit, exclude);
    case Value_Numeric_Type.TEMPERATURE:
      return enumValuesClean(Value_Numeric_Temperature_TemperatureUnit, exclude);
    case Value_Numeric_Type.PRESSURE:
      return enumValuesClean(Value_Numeric_Pressure_PressureUnit, exclude);
    case Value_Numeric_Type.DISTANCE:
      return enumValuesClean(Value_Numeric_Distance_DistanceUnit, exclude);
    case Value_Numeric_Type.DURATION:
      return enumValuesClean(Value_Numeric_Duration_DurationUnit, exclude);
    case Value_Numeric_Type.FUEL_EFFICIENCY:
      return enumValuesClean(Value_Numeric_FuelEfficiency_FuelEfficiencyUnit, exclude);
    case Value_Numeric_Type.MASS:
      return enumValuesClean(Value_Numeric_Mass_MassUnit, exclude);
    case Value_Numeric_Type.PERCENTAGE:
      return enumValuesClean(Value_Numeric_Percentage_PercentageUnit, exclude);
    case Value_Numeric_Type.RATE:
      return enumValuesClean(Value_Numeric_Rate_RateUnit, exclude);
    case Value_Numeric_Type.ROTATION_SPEED:
      return enumValuesClean(Value_Numeric_RotationSpeed_RotationSpeedUnit, exclude);
    case Value_Numeric_Type.VOLTAGE:
      return enumValuesClean(Value_Numeric_Voltage_VoltageUnit, exclude);
    default: {
      return [];
    }
  }
}

export function unitByType(numericValueType: Value_Numeric_Type | undefined, value: string): Field_Numeric_Unit | [] {
  switch (numericValueType) {
    case Value_Numeric_Type.BATTERY_LEVEL:
      return {
        unit: {
          $case: 'batteryLevel',
          batteryLevel: Value_Numeric_BatteryLevel_BatteryLevelUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.VOLUME:
      return {
        unit: {
          $case: 'volume',
          volume: Value_Numeric_Volume_VolumeUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.FLOW:
      return {
        unit: {
          $case: 'flow',
          flow: Value_Numeric_Flow_FlowUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.SPEED:
      return {
        unit: {
          $case: 'speed',
          speed: Value_Numeric_Speed_SpeedUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.TEMPERATURE:
      return {
        unit: {
          $case: 'temperature',
          temperature: Value_Numeric_Temperature_TemperatureUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.PRESSURE:
      return {
        unit: {
          $case: 'pressure',
          pressure: Value_Numeric_Pressure_PressureUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.DISTANCE:
      return {
        unit: {
          $case: 'distance',
          distance: Value_Numeric_Distance_DistanceUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.DURATION:
      return {
        unit: {
          $case: 'duration',
          duration: Value_Numeric_Duration_DurationUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.FUEL_EFFICIENCY:
      return {
        unit: {
          $case: 'fuelEfficiency',
          fuelEfficiency: Value_Numeric_FuelEfficiency_FuelEfficiencyUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.MASS:
      return {
        unit: {
          $case: 'mass',
          mass: Value_Numeric_Mass_MassUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.PERCENTAGE:
      return {
        unit: {
          $case: 'percentage',
          percentage: Value_Numeric_Percentage_PercentageUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.RATE:
      return {
        unit: {
          $case: 'rate',
          rate: Value_Numeric_Rate_RateUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.ROTATION_SPEED:
      return {
        unit: {
          $case: 'rotationSpeed',
          rotationSpeed: Value_Numeric_Speed_SpeedUnit[value],
        },
      } as Field_Numeric_Unit;
    case Value_Numeric_Type.VOLTAGE:
      return {
        unit: {
          $case: 'voltage',
          voltage: Value_Numeric_Voltage_VoltageUnit[value],
        },
      } as Field_Numeric_Unit;
    default: {
      return [];
    }
  }
}

export const transformFormValues = (flatValues: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  Object.entries(flatValues).forEach(([key, value]) => {
    if (key.includes('.')) {
      const parts = key.split('.');
      let current = result;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part] as Record<string, unknown>;
      }

      current[parts[parts.length - 1]] = value;
    } else {
      result[key] = value;
    }
  });

  return result;
};
